<script setup>
import { computed, watch } from 'vue'
import { useCurrentEntryStore } from '@/stores/currentEntry'
import { useAppUserStore } from '@/stores/appUser'
import { useUiStore } from '@/stores/ui'
import { createReferenceSheetTitle } from "@/functions/resultSuggestion.js"
import { useI18n } from "vue-i18n";
const { t } = useI18n();

const currentEntryStore = useCurrentEntryStore()
const appUserStore = useAppUserStore()
const uiStore = useUiStore()

//Reference Suggestions
const allCclRefSheets = computed(() => {
  const resultExpressions = currentEntryStore.currCclSuggestionsFromResults?.expressions || []
  return resultExpressions
    // .filter(expression => expression.refSheetSuggestions)
    .filter(expression => expression.refSheet)
    .filter(expression => !(expression.refSheet.presentation0 === '' && expression.refSheet.etio0 === '' && expression.refSheet.complic0 === ''))
})

const uniqueRefSheets = computed(() => {
  const seenIds = new Set()
  const uniqueRefSheetList = []
  allCclRefSheets.value.forEach(({ refSheet }) => {
    const id = refSheet.appUserResultReferenceId
    if (!seenIds.has(id)) {
      seenIds.add(id)
      uniqueRefSheetList.push(refSheet)
    }
  })
  return uniqueRefSheetList
})


// Region Suggestions
const preferences = computed(() => appUserStore.currentAppUserData?.preferences)

const baseSuggestions = computed(() => {
  const prefs = preferences.value
  return [
    'Measures',
    ...((prefs?.suggestionsShowEnt ?? true) ? ['Ent'] : []),
    ...((prefs?.suggestionsShowThorax ?? true) ? ['Thorax'] : []),
    ...((prefs?.suggestionsShowAbdomen ?? true) ? ['Abdomen'] : []),
    ...((prefs?.suggestionsShowNeuro ?? true) ? ['CranialNerves1', 'NeuroPeriph', 'MotorCoordination'] : []),
    ...((prefs?.suggestionsShowLymph ?? true) ? ['Lymph'] : []),
  ]
})

function switchSuggestionGroupAndRefSheet(refSheet) {
  const refSheetId = refSheet.appUserResultReferenceId
  if (currentEntryStore.activeResultSuggestion !== refSheetId) {
    uiStore.setReferenceTab("Sheet")
    uiStore.setCurrRefSheet(refSheet)
  }
  switchSuggestionGroup(refSheetId)
}

function switchSuggestionGroup(id) {
  currentEntryStore.setIsManualSet(true)
  if (currentEntryStore.activeResultSuggestion !== id) {
    currentEntryStore.setActiveResultSuggestion(id)
  } else {
    currentEntryStore.clearActiveResultSuggestion()
  }
}

const resultSuggestionKeys = computed(() => {
  return currentEntryStore.currCclResultKeys?.resultSuggestionKeys || []
})

async function toggleResultSuggestionList() {
  if (currentEntryStore.isManualSet) return;
  const lastAutoSuggestion = uniqueRefSheets.value?.[0]?.appUserResultReferenceId || resultSuggestionKeys.value?.[0] || "Measures"
  currentEntryStore.setActiveResultSuggestion(lastAutoSuggestion)
}

function changedCclForResult() {
  currentEntryStore.setIsManualSet(false);
  currentEntryStore.clearActiveResultSuggestion()
}

watch(
  () => currentEntryStore.currentCcl,
  () => {
    changedCclForResult()
  },
  { immediate: true }
)

watch(
  () => [
    appUserStore.currentAppUserData,
    currentEntryStore.currCclSuggestionsFromResults,
    currentEntryStore.currCclResultKeys,
  ],
  () => {
    toggleResultSuggestionList()
  },
  { immediate: true }
)

</script>

<template>

  <div class="es-suggestion-tag">
    <!-- GroupSuggestions -->
    <div class="pad-s">
      <div class="row h-20">
        <switchGroup styles="size-m padButton-s" :active="currentEntryStore.activeResultSuggestion === 'GroupSuggestions'" @click="switchSuggestionGroup('GroupSuggestions')">
          <div class="row h-16">
            <img src="@/assets/images/shareIcons/list_dash.svg" class="mright-04" :class="currentEntryStore.activeResultSuggestion === 'GroupSuggestions' ? 'img-white' : 'img-primary'">
            <text-item :txt="t('patient.suggestion.sign.Groups')" :class="currentEntryStore.activeResultSuggestion === 'GroupSuggestions' ? 'txt-white' : 'txt-primary'" :styles="'font-mBold'"/>
          </div>
        </switchGroup>
      </div>
    </div>    
    <!-- Region Suggestions -->
    <template v-if="appUserStore.cachedResultSuggestions" >
        <template v-for="(suggGroupValue, suggGroupKey, index) in appUserStore.cachedResultSuggestions" :key="index">
          <div v-if="baseSuggestions.includes(suggGroupKey) || resultSuggestionKeys.includes(suggGroupKey)" class="pad-s">
            <!-- Suggestion Group -->
            <div class="row h-20">
              <switchGroup styles="size-m padButton-s" :active="currentEntryStore.activeResultSuggestion === suggGroupKey" @click="switchSuggestionGroup(suggGroupKey)">
                <div class="row h-16">
                  <img src="@/assets/images/consultations/sparkles.svg" class="mright-04" :class="currentEntryStore.activeResultSuggestion === suggGroupKey ? 'img-white' : 'img-primary'" v-if="resultSuggestionKeys.includes(suggGroupKey)">
                  <text-item :txt="t(`patient.suggestion.sign.${suggGroupKey}`, suggGroupKey)" :class="currentEntryStore.activeResultSuggestion === suggGroupKey ? 'txt-white' : 'txt-primary'" :styles="'font-mBold'"/>
                </div>
              </switchGroup>
            </div>
          </div>
        </template>
      </template>
      <!-- RefSheet Suggestions -->
      <template v-if="allCclRefSheets">
          <template v-for="(refSheet, index) in uniqueRefSheets" :key="index">
            <!-- Suggestion Group -->
            <div class="row pad-s">
              <switchGroup styles="size-m padButton-s" :active="currentEntryStore.activeResultSuggestion === refSheet.appUserResultReferenceId"  @click="switchSuggestionGroupAndRefSheet(refSheet)">
                <div class="row h-16">
                  <img src="@/assets/images/consultations/sparkles.svg" class="mright-04" :class="currentEntryStore.activeResultSuggestion === refSheet.appUserResultReferenceId? 'img-white' : 'img-primary'">
                  <text-item :txt="createReferenceSheetTitle(refSheet)" :class="currentEntryStore.activeResultSuggestion === refSheet.appUserResultReferenceId ? 'txt-white' : 'txt-primary'" styles="font-mBold"/>
                </div>
              </switchGroup>
            </div>
          </template>
      </template>
  </div>
</template>

<style scoped>
.es-suggestion-tag {
  display: flex;
  flex-wrap: wrap;
}

.h-20 {
  height: 20px;
}

.h-16 {
  height: 16px;
}
</style>